import Vue from 'vue';
import Vuex from 'vuex';

// Import vuex store modules
import user from './modules/user';
import * as types from './mutation-types';

Vue.use(Vuex);

/**
 * Export Vuex store with strict mode and
 * middleware depending on config.ENV.debug setting for current environment
 *
 * @type {Vuex}
 */
export const store = new Vuex.Store({
    modules: {
        user,
    },

    state: {
        bundles: [],
        stb: [],
        checkout: {},
        selectedLocale: 'de',
        preloaderAxiosRequestScreen: false,
        checkedLimitAgreement: false,
        chosenTv: {},
        bonusTv: 0,
        usePrepaidSteps: false,
        isGeneralCheckout: true,
        termsAccepted: false,
        steps: {
            general: {
                '1': {
                    title: '1. ',
                    translationString: 'checkout2.chosenPackage',
                },
                '3': {
                    title: '2. ',
                    translationString: 'checkout2.mtelAccount',
                },
                '4': {
                    title: '3. ',
                    translationString: 'checkout2.personalInformation',
                },
                '4a': {
                    title: '4. ',
                    translationString: 'checkout2.address',
                },
                '5': {
                    title: '5. ',
                    translationString: 'checkout5.document',
                },
            },
            prepaid: {
                '3': {
                    title: '1. ',
                    translationString: 'checkout2.mtelAccount',
                },
                '4': {
                    title: '2. ',
                    translationString: 'checkout2.personalInformation',
                },
                '4a': {
                    title: '3. ',
                    translationString: 'checkout2.address',
                },
                '5': {
                    title: '5. ',
                    translationString: 'checkout5.document',
                },
            }
        }    
    },

    getters: {
        bundlesCode: (state) => state.checkout.bundles !== undefined ? state.checkout.bundles : state.bundles,
        stb: (state) => state.checkout.tv_device.length ? state.checkout.tv_device : state.stb,
        checkout: (state) => state.checkout,
        preloaderScreen: (state) => state.preloaderAxiosRequestScreen,
        checkedLimitAgreement: (state) => state.checkedLimitAgreement,
        chosenTv: (state) => state.chosenTv,
        bonusTv: (state) => state.bonusTv,
        usePrepaidSteps: (state) => state.usePrepaidSteps,
        isGeneralCheckout: (state) => state.isGeneralCheckout,
        termsAccepted: (state) => state.termsAccepted,
        steps: (state) => state.steps,
    },

    mutations: {
        [types.SET_CHOSEN_TV](state, tv) {
            state.chosenTv = tv;
            if (tv.code) {
                state.checkout.tv_tariff = tv.code;
            } else {
                delete state.checkout.tv_tariff;
            }
        },
        [types.SET_CHOSEN_TV_BONUS](state, bonus) {
            state.bonusTv = bonus;
        },

        [types.SET_CHOSEN_TV_TARIFF](state, tvTariff) {
            state.checkout.tv_tariff = tvTariff.code;
        },

        [types.SET_USE_PREPAID_STEPS](state, value) {
            state.usePrepaidSteps = value;
        },

        [types.SET_IS_GENERAL_CHECKOUT](state, value) {
            state.isGeneralCheckout = value;
        },

        [types.SET_TERMS_ACCEPTED](state, value) {
            state.termsAccepted = value;
        },

        [types.SET_CHECKED_LIMIT_AGREEMENT](state, payload) {
            state.checkedLimitAgreement = payload;
        },
        /**
         * This update bundles
         * @param state
         * @param payload
         */
        [types.UPDATE_BUNDLES](state, payload) {
            if (payload.code && state.bundles.indexOf(payload.code) === -1) {
                state.bundles.push(payload.code);
                state.checkout.bundles = state.bundles;
                state.checkout.addon_type = 'Bundle';
            } else {
                state.bundles = state.bundles.filter(bundle => bundle !== payload.code);
                state.checkout.bundles = state.bundles;
        
                if (! state.checkout.bundles.length) {
                    delete state.checkout.addon_type;
                }
            }
        },
        /**
         * This update stb
         * @param state
         * @param payload
         */
        [types.UPDATE_STB](state, payload) {
            if (payload.tvDevices.length) {
                state.checkout.type = 'Tv Device';
                state.checkout.tv_device = payload.tvDevices;
            } else {
                state.checkout.type = 'TV Tariff';
                delete state.checkout.tv_device;
            }
        },

        /**
         * This save checkout from server through blade file
         * @param state
         * @param payload
         */
        [types.SET_CHECKOUT_DATA](state, payload) {
            state.checkout = payload;
        },

        [types.SET_LIMIT_CHECKOUT](state, limit) {
            if (limit) {
                state.checkout.limit = limit;
            } else {
                delete state.checkout.limit;
            }
        },

        [types.SET_POST_BILL_CHECKOUT](state, postBill) {
            if (postBill) {
                state.checkout.postBill = postBill;
            } else {
                delete state.checkout.postBill;
            }
        },

        [types.SET_INSURANCE_CHECKOUT](state, insurance) {
            state.checkout.insurance = insurance;
        },

        /**
         * Set to true preloader screen
         * @param state
         */
        [types.TOGGLE_PRELOADER_AXIOS_REQUEST_SCREEN_TO_TRUE](state) {
            state.preloaderAxiosRequestScreen = true;
        },

        /**
         * Set to false preloader screen
         * @param state
         */
        [types.TOGGLE_PRELOADER_AXIOS_REQUEST_SCREEN_TO_FALSE](state) {
            state.preloaderAxiosRequestScreen = false;
        },
    },

    actions: {
        updateBundles({commit}, payload) {
            commit('UPDATE_BUNDLES', payload);
        },

        updateStb({commit}, payload) {
            commit('UPDATE_STB', payload);
        },

        updateCheckout({commit}, payload) {
            commit('SET_CHECKOUT_DATA', payload);
        },

        updateLimit({commit}, payload) {
            commit('SET_LIMIT_CHECKOUT', payload);
        },

        updatePostBill({commit}, payload) {
            commit('SET_POST_BILL_CHECKOUT', payload);
        },

        updateInsurance({commit}, payload) {
            commit('SET_INSURANCE_CHECKOUT', payload);
        }
    },

    strict: false,
});
